import React from "react";
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { isAdmin, isFinancialManager, isMediaBuyingOnly } from "utils/roles";
import { buzz } from "utils/haptics";
import { useGetMbUnreadCountQuery } from "state/api";
import useVirtualKeyboard from "hooks/useVirtualKeyboard";
// Per-role bar variants live in navConfig.js (shared with the Sidebar and the
// Ctrl+K CommandPalette): universal Home/Leads/Settings for lead-facing roles
// (each role's router redirects the entry routes to its prefixed page), a
// module bar for media-buying-only roles, and the 5-item admin bar.
import { getBottomNavItems } from "../navConfig";

function activeValue(pathname, variant) {
  const p = (pathname || "").toLowerCase();
  if (variant === "mb") {
    if (p.includes("media-buying/inbox")) return "inbox";
    if (p.includes("media-buying")) return "mediabuying";
    if (p.includes("setting")) return "settings";
    return "mediabuying";
  }
  if (variant === "fm") {
    // Order matters: "clientsetting"/"clientpricing" must match BEFORE the
    // generic "client" substring so Pricing doesn't light up Clients.
    // (/financialclient/:id falls through to the clients tab on purpose.)
    if (p.includes("clientsetting") || p.includes("clientpricing"))
      return "pricing";
    if (p.includes("budgettracker")) return "budget";
    if (p.includes("invoice")) return "invoices";
    if (p.includes("client")) return "clients";
    return "dashboard";
  }
  if (variant === "admin") {
    if (p.includes("setting")) return "settings";
    // Check media-buying before "client" — the MB module has its own
    // /media-buying/clients panel that must light up "Media", not "Clients".
    if (p.includes("media-buying")) return "mediabuying";
    if (p.includes("client")) return "clients";
    if (p.includes("lead")) return "leads";
    return "dashboard";
  }
  if (p.includes("setting")) return "settings";
  if (p.includes("lead")) return "leads";
  return "dashboard";
}

/**
 * Mobile bottom tab bar. Rendered by Layout only on phone-width screens.
 *
 * Media Buyer / Media Buying Head get a module-specific bar (Media Buying /
 * Inbox / Settings); every other role keeps the universal Home / Leads /
 * Settings. Admins keep the universal bar — they roam the whole CRM, not just
 * the media-buying module.
 *
 * Colors are set EXPLICITLY (dark surface + visible label colors) rather than
 * relying on MUI defaults — this theme overrides background.default/alt to dark
 * values but never sets palette.mode:"dark", so MUI's default `background.paper`
 * and action colors resolve to a light/white scheme that would render this bar
 * white with invisible unselected items.
 */
export default function BottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useSelector((state) => state.auth.user);
  const mbOnly = isMediaBuyingOnly(user);
  const admin = isAdmin(user);
  // Unread inbox badge — only for the MB bar; pause polling when the tab is
  // hidden (mirrors the Sidebar's badge query).
  const { data: mbUnreadData } = useGetMbUnreadCountQuery(undefined, {
    skip: !mbOnly,
    pollingInterval: 60000,
    skipPollingIfUnfocused: true,
  });
  const mbUnread = mbUnreadData?.count || 0;
  // While the on-screen keyboard is up the bar hides. Above the keyboard it
  // is dead space, and on iOS it is also the bug: a fixed bar that was laid
  // out against the keyboard-shrunk viewport stays mid-screen after the
  // keyboard is dismissed, scrolling with the page. Hiding it and re-showing
  // it after the keyboard closes lays it out fresh at the real bottom edge.
  const keyboardOpen = useVirtualKeyboard();

  if (!user) return null;

  const variant = mbOnly
    ? "mb"
    : isFinancialManager(user)
      ? "fm"
      : admin
        ? "admin"
        : "default";
  const items = getBottomNavItems(user);
  const value = activeValue(pathname, variant);

  return (
    <Paper
      square
      elevation={0}
      sx={(t) => ({
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: t.zIndex.appBar,
        display: keyboardOpen ? "none" : undefined,
        // Solid raised surface is the base; browsers with backdrop-filter get
        // the frosted-glass treatment below (content scrolls blurred under it).
        backgroundColor:
          t.palette.extras?.surface?.raised || t.palette.background.default || "#1a2a27",
        backgroundImage: "none",
        borderTop: `1px solid ${
          t.palette.extras?.surface?.borderSubtle || "rgba(255,255,255,0.10)"
        }`,
        "@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))":
          {
            backgroundColor:
              t.palette.extras?.glass?.raised || "rgba(33, 52, 50, 0.78)",
            backdropFilter:
              t.palette.extras?.glass?.blur || "saturate(1.5) blur(16px)",
            WebkitBackdropFilter:
              t.palette.extras?.glass?.blur || "saturate(1.5) blur(16px)",
            borderTopColor:
              t.palette.extras?.glass?.edge || "rgba(255, 255, 255, 0.10)",
          },
        pb: "var(--safe-area-bottom, 0px)",
      })}
    >
      <BottomNavigation
        showLabels
        value={value}
        onChange={(_, next) => {
          const item = items.find((i) => i.value === next);
          if (item && next !== value) buzz();
          if (item) navigate(item.path);
        }}
        sx={(t) => ({
          backgroundColor: "transparent",
          "& .MuiBottomNavigationAction-root": {
            color: t.palette.extras?.surface?.textMid || "rgba(255,255,255,0.6)",
            minWidth: 0,
            position: "relative",
            zIndex: 0,
          },
          // Pill indicator that springs in behind the selected icon.
          "& .MuiBottomNavigationAction-root::before": {
            content: '""',
            position: "absolute",
            top: 6,
            left: "50%",
            width: 42,
            height: 24,
            marginLeft: "-21px",
            borderRadius: 999,
            backgroundColor:
              t.palette.extras?.accent?.subtle || "rgba(127, 176, 154, 0.14)",
            opacity: 0,
            transform: "scale(0.6)",
            transition: `opacity 180ms ease, transform 220ms ${
              t.palette.extras?.motion?.spring ||
              "cubic-bezier(0.2, 0.9, 0.3, 1.2)"
            }`,
            zIndex: -1,
          },
          "& .MuiBottomNavigationAction-root.Mui-selected::before": {
            opacity: 1,
            transform: "scale(1)",
          },
          "& .MuiBottomNavigationAction-root.Mui-selected": {
            color: t.palette.extras?.accent?.main || "#7fb09a",
          },
          "& .MuiBottomNavigationAction-label.Mui-selected": {
            color: t.palette.extras?.accent?.main || "#7fb09a",
            fontWeight: 600,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "& .MuiBottomNavigationAction-root::before": {
              transition: "none",
            },
          },
        })}
      >
        {items.map((i) => (
          <BottomNavigationAction
            key={i.value}
            value={i.value}
            label={i.label}
            icon={
              i.badge && mbUnread > 0 ? (
                <Badge color="error" badgeContent={mbUnread} max={99}>
                  <i.Icon />
                </Badge>
              ) : (
                <i.Icon />
              )
            }
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
}
